import { useId, useState } from 'react'
import { BRAND } from '../brand'
import { isValidEmail, subscribe } from '../subscribe'

/**
 * "I'm starting a monthly note" — the one place this product asks for anything.
 *
 * It sits below the result, past the share button, below the fold. That
 * placement is the whole design: the funnel this product depends on is link →
 * move a slider → see your number → share it, and nothing may sit in front of
 * it. Someone who never scrolls this far never learns the form exists and loses
 * nothing by it.
 *
 * ── The copy promises less than it could, on purpose ────────────────────────
 *
 * An earlier draft read "Once a month, what they did to a plan like yours."
 * There is no newsletter and no way to send one yet, so a cadence promise is a
 * claim about future conduct that cannot currently be performed — and the
 * advertising rules that governs are in force today, unlike the data-protection
 * ones, which commence in 2027. So it says a note is starting and that it is not
 * written yet. Truthful, still worth an address, and nothing to fail to keep.
 *
 * ── Why this holds its own state ────────────────────────────────────────────
 *
 * `RiskProfilePicker` is fully controlled because its value belongs to the plan.
 * This one belongs to nobody — `App.tsx` has no use for a half-typed address —
 * so it follows `Slider`'s local `editing`/`draft` precedent instead.
 *
 * ── There is no consent checkbox, and there was ─────────────────────────────
 *
 * One shipped with this and it was redundant. The convention it copied is about
 * BUNDLING: a tick-box is needed when permission to email rides inside a form
 * whose real purpose is something else, because submitting only proves the
 * reader wanted the other thing. This form has no other thing — one heading, one
 * field, one button — so pressing the button is already the clear affirmative
 * act, and asking again three inches lower asked the same question twice.
 *
 * Two consequences worth keeping. The button says "Email me the note" rather
 * than "Send it": it now carries the consent the box used to, so it has to name
 * what pressing it agrees to. And the notice — what is sent, and how to get off
 * the list — is its own paragraph rather than a checkbox label, because that
 * text was the one thing the box genuinely bought and it must not lose
 * prominence on the way out.
 *
 * What is stored does not weaken. `consent: true` still goes to the server with
 * every submission, because it was never the tick that made it true.
 */

type Status = 'idle' | 'sending' | 'done' | 'invalid' | 'unavailable' | 'failed'

export function EmailCapture() {
  const emailId = useId()
  const errorId = useId()
  const noticeId = useId()

  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [announcement, setAnnouncement] = useState('')

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (status === 'sending') return

    // Checked here as well as on the server. The server's answer is the one
    // that counts; this one just saves a round trip and says so sooner.
    if (!isValidEmail(email)) {
      setStatus('invalid')
      return
    }

    /* The address as submitted, held across the await. Without it, someone who
       submits one address and edits the field while the request is in flight
       gets "You're on the list" — a confirmation about an address that was
       never sent. The field is frozen during 'sending' as well; this is the
       belt for the case where it is not. */
    const submitted = email
    setStatus('sending')
    const outcome = await subscribe({ email: submitted, consent: true })
    if (submitted !== email) return

    setStatus(outcome === 'ok' ? 'done' : outcome)
    if (outcome === 'ok')
      setAnnouncement("You're on the list. The first note comes when it's written.")
  }

  /* The live region is rendered ALWAYS and filled on success, never mounted
     together with its text. A polite region that appears already populated is
     not a mutation of an existing region, and most screen readers announce
     nothing at all — which would make the one confirmation the reader gets the
     one they cannot hear. */
  const liveRegion = (
    <p aria-live="polite" role="status" className="sr-only">
      {announcement}
    </p>
  )

  if (status === 'done') {
    return (
      <section
        aria-label="Monthly note"
        className="mt-6 rounded-[var(--radius-lg)] border border-[color:var(--c-border-subtle)] px-5 py-4"
      >
        {liveRegion}
        <p className="text-[length:var(--text-label)] text-[color:var(--c-text-secondary)]">
          You're on the list. The first note comes when it's written.
        </p>
      </section>
    )
  }

  return (
    <section
      aria-label="Monthly note"
      className="mt-6 rounded-[var(--radius-lg)] border border-[color:var(--c-border-subtle)] px-5 py-4"
    >
      {liveRegion}
      <h2 className="text-[length:var(--text-label)] text-[color:var(--c-text-primary)]">
        I'm starting a monthly note
      </h2>
      <p className="mt-1 text-[length:var(--text-label)] text-[color:var(--c-text-tertiary)]">
        on what the markets did to a plan like yours. It isn't written yet — leave your
        email and you'll get the first one.
      </p>

      <form onSubmit={submit} className="mt-3 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={emailId} className="sr-only">
            Your email address
          </label>
          <input
            id={emailId}
            type="email"
            inputMode="email"
            autoComplete="email"
            /* The consent box used to gate the button, so submitting an empty
               form was impossible. Without it a first click on an untouched form
               said "That doesn't look like an email address" about an address
               nobody had typed. `required` hands the empty case to the browser,
               which says the right thing in the reader's own language, and
               leaves the JS check for addresses that are present and wrong. */
            required
            placeholder="your@email.com"
            value={email}
            disabled={status === 'sending'}
            aria-invalid={status === 'invalid'}
            /* The notice is ALWAYS referenced. It stopped being a checkbox label
               when the box went, and a bare paragraph inside a form is attached
               to nothing — in forms mode a screen reader goes field, button, and
               never reads it. It is the only place consent is disclosed now, so
               losing it for exactly the users the checkbox was chosen for would
               undo the reason the box existed at all. */
            aria-describedby={[
              noticeId,
              status === 'idle' || status === 'sending' ? null : errorId,
            ]
              .filter(Boolean)
              .join(' ')}
            onChange={(event) => {
              setEmail(event.target.value)
              if (status !== 'idle' && status !== 'sending') setStatus('idle')
            }}
            className="min-w-0 flex-1 rounded-[var(--radius-sm)] border border-[color:var(--c-border)] bg-[color:var(--c-bg-elevated)] px-3 py-2 text-[length:var(--text-label)] text-[color:var(--c-text-primary)]"
          />
          <button
            type="submit"
            disabled={status === 'sending'}
            className="rounded-[var(--radius-full)] border border-[color:var(--c-border)] px-4 text-[length:var(--text-label)] text-[color:var(--c-text-primary)] transition-colors duration-[var(--dur-fast)] enabled:hover:bg-[color:var(--c-bg-hover)] disabled:opacity-50"
            style={{ height: 'var(--touch-target)' }}
          >
            {status === 'sending' ? 'Sending…' : 'Email me the note'}
          </button>
        </div>

        <p
          id={noticeId}
          className="text-[length:var(--text-micro)] leading-relaxed text-[color:var(--c-text-muted)]"
        >
          Only your email address is sent — never your plan or your numbers. Write to{' '}
          <a
            href={`mailto:${BRAND.supportEmail}`}
            className="text-[color:var(--c-accent)] underline underline-offset-2"
          >
            {BRAND.supportEmail}
          </a>{' '}
          any time and I'll remove you.
        </p>

        {(status === 'invalid' || status === 'unavailable' || status === 'failed') && (
          <p
            id={errorId}
            role="alert"
            className="text-[length:var(--text-micro)] text-[color:var(--c-negative)]"
          >
            {status === 'invalid'
              ? "That doesn't look like an email address."
              : status === 'unavailable'
                ? "Signups aren't working right now — that's on me, not you. Nothing was sent."
                : "That didn't send. Try again in a moment."}
          </p>
        )}
      </form>
    </section>
  )
}
